/**
 * @file product-attributes.ts
 * @description Характеристики товара «название — значение» (phase19.16 Р2а,
 * SB33): строки формы, разбор из `Product.attributes`, проверка и тело
 * запроса. Хранятся массивом `[{ key, value }]` — порядок строк = порядок в
 * таблице на странице товара. Пустые строки не сохраняются.
 */

import type { ProductAttribute } from "../../../lib/api-catalog";

/** Не больше строк в карточке товара. */
export const ATTRIBUTES_MAX = 30;
/** Длина названия. */
export const ATTRIBUTE_KEY_MAX = 60;
/** Длина значения. */
export const ATTRIBUTE_VALUE_MAX = 200;

/** Строка формы: `id` нужен только списку (перетаскивание, ключи React). */
export interface ProductAttributeRow extends ProductAttribute {
  id: string;
}

let rowSeq = 0;

/** Новая строка формы. */
export function newAttributeRow(key = "", value = ""): ProductAttributeRow {
  rowSeq += 1;
  return { id: `attr-${rowSeq}`, key, value };
}

/** Простое значение → строка; вложенные объекты не показываем. */
function asText(v: unknown): string | null {
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return null;
}

/**
 * `Product.attributes` → характеристики. Принимает массив `{ key, value }`
 * и, на всякий случай, старый вид объекта `{ "Вес": "50 г" }`; всё прочее —
 * пусто (поле `Json`, раньше кабинет его не писал).
 */
export function parseAttributes(raw: unknown): ProductAttribute[] {
  if (Array.isArray(raw)) {
    return raw.flatMap((item: unknown) => {
      if (!item || typeof item !== "object") return [];
      const rec = item as Record<string, unknown>;
      const key = asText(rec.key);
      const value = asText(rec.value);
      return key === null || value === null ? [] : [{ key, value }];
    });
  }
  if (raw && typeof raw === "object") {
    return Object.entries(raw as Record<string, unknown>).flatMap(([key, v]) => {
      const value = asText(v);
      return value === null ? [] : [{ key, value }];
    });
  }
  return [];
}

/** Строки без крайних пробелов, полностью пустые — выброшены. */
export function cleanAttributes(rows: readonly ProductAttribute[]): ProductAttribute[] {
  return rows
    .map((r) => ({ key: r.key.trim(), value: r.value.trim() }))
    .filter((r) => r.key !== "" || r.value !== "");
}

/**
 * Замечания по строкам (индекс как в `rows`; `null` — строка в порядке).
 * Полностью пустая строка замечаний не получает — её просто не сохраним.
 */
export function attributeRowErrors(rows: readonly ProductAttribute[]): Array<string | null> {
  const seen = new Set<string>();
  return rows.map((r) => {
    const key = r.key.trim();
    const value = r.value.trim();
    if (!key && !value) return null;
    if (!key) return "Укажите название";
    if (!value) return "Укажите значение";
    if (key.length > ATTRIBUTE_KEY_MAX) return `Название — не длиннее ${ATTRIBUTE_KEY_MAX} символов`;
    if (value.length > ATTRIBUTE_VALUE_MAX) return `Значение — не длиннее ${ATTRIBUTE_VALUE_MAX} символов`;
    const norm = key.toLocaleLowerCase("ru-RU");
    if (seen.has(norm)) return "Такое название уже есть";
    seen.add(norm);
    return null;
  });
}

/** Первое замечание для проверки перед сохранением; `null` — можно сохранять. */
export function attributesError(rows: readonly ProductAttribute[]): string | null {
  if (cleanAttributes(rows).length > ATTRIBUTES_MAX) {
    return `Характеристик — не больше ${ATTRIBUTES_MAX}`;
  }
  const errors = attributeRowErrors(rows);
  const index = errors.findIndex((e) => e !== null);
  if (index < 0) return null;
  const text = errors[index] ?? "";
  return `Характеристики, строка ${index + 1}: ${text.charAt(0).toLocaleLowerCase("ru-RU")}${text.slice(1)}.`;
}

/**
 * `attributes` для запроса. Новый товар — массив или ничего. Правка — поле
 * уходит, только если характеристики поменялись (не перезаписываем данные
 * старого вида без нужды); всё убрали — `null`.
 */
export function attributesForPayload(
  rows: readonly ProductAttribute[],
  originalRaw: unknown,
  isNew: boolean,
): ProductAttribute[] | null | undefined {
  const next = cleanAttributes(rows);
  if (isNew) return next.length > 0 ? next : undefined;
  const before = cleanAttributes(parseAttributes(originalRaw));
  if (JSON.stringify(next) === JSON.stringify(before)) return undefined;
  return next.length > 0 ? next : null;
}

/** Переставить строку `from` на место `to` (перетаскивание). */
export function moveAttributeRow<T>(rows: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) return [...rows];
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return next;
}

/** id поля «Название» строки — для фокуса на новой строке. */
export function attributeKeyInputId(rowId: string): string {
  return `pa-${rowId}-key`;
}
